<template>
  <div
    class="aircraft-events-map"
    :class="{
      'aircraft-events-map_events-only': eventsOnly,
      'aircraft-events-map_fullscreen': isFullscreen,
    }"
  >
    <div v-if="!eventsOnly" class="aircraft-events-map__toolbar">
      <router-link
        :to="{name: routes.ROUTE_PACS_AIRCRAFT_ANALYTICS}"
        class="aircraft-events-map__back"
      >
        <svg aria-hidden="true">
          <use xlink:href="#icon-arrow-right" />
        </svg>
        <span>{{ $t('backToCameraList') }}</span>
      </router-link>
    </div>

    <div
      ref="content"
      class="aircraft-events-map__content"
      :class="{
        'aircraft-events-map__content_events-collapsed': isEventsBarCollapsed,
        'aircraft-events-map__content_events-only': eventsOnly,
      }"
      :style="eventsContentStyle"
    >
      <div v-if="!eventsOnly" class="aircraft-events-map__map-container">
        <v-map
          ref="map"
          :bounds="bounds"
          :center="defaultCenter"
          :min-zoom="minZoom"
          :options="mapOptions"
          :zoom="zoom"
          @click="addMapSelectionPoint"
          @moveend="updateMapOverviewState"
        >
          <v-control-zoom zoom-in-title="" zoom-out-title="" position="verticalcenterright" />
          <v-tile-layer :attribution="attributionLayer" :url="mapTilesUrl" />
          <v-control-attribution position="bottomright" prefix="" />

          <v-polygon
            v-if="isMapSelectionMode && mapSelectionPoints.length >= 3"
            :lat-lngs="mapSelectionPoints"
            :interactive="false"
            color="#d92d2d"
            :fill-opacity="0.12"
            :weight="2"
          />
          <v-polyline
            v-else-if="isMapSelectionMode && mapSelectionPoints.length === 2"
            :lat-lngs="mapSelectionPoints"
            :interactive="false"
            color="#d92d2d"
            :weight="2"
          />
          <v-marker
            v-for="(point, index) in mapSelectionPoints"
            :key="`selection-point-${index}`"
            :lat-lng="point"
            :icon="selectionPointIcon"
            :draggable="!isSavingMapSelection"
            :z-index-offset="eventMarkerZIndex + 1"
            @click="stopMapSelectionPointClick"
            @drag="moveMapSelectionPoint(index, $event)"
            @dragend="moveMapSelectionPoint(index, $event)"
          />

          <v-circle-marker
            v-for="camera in warningEventCountMapCameras"
            :key="`aircraft-event-count-warning-${camera.number}`"
            :bubbling-mouse-events="false"
            class-name="aircraft-events-map__event-count-circle aircraft-events-map__event-count-circle_warning"
            color="#d92d2d"
            :fill="true"
            fill-color="#d92d2d"
            :fill-opacity="0.08"
            :interactive="false"
            :lat-lng="cameraLatLng(camera)"
            :opacity="0.42"
            pane="overlayPane"
            :radius="30"
            :weight="2"
          />
          <v-circle-marker
            v-for="camera in alarmEventCountMapCameras"
            :key="`aircraft-event-count-alarm-${camera.number}`"
            :bubbling-mouse-events="false"
            class-name="aircraft-events-map__event-count-circle aircraft-events-map__event-count-circle_alarm"
            color="#d92d2d"
            :fill="true"
            fill-color="#d92d2d"
            :fill-opacity="0.24"
            :interactive="false"
            :lat-lng="cameraLatLng(camera)"
            :opacity="1"
            pane="overlayPane"
            :radius="20"
            :weight="3"
          />

          <v-circle-marker
            v-for="camera in flashingMapCameras"
            :key="`aircraft-event-halo-outer-${camera.number}`"
            :bubbling-mouse-events="false"
            class-name="aircraft-events-map__event-halo aircraft-events-map__event-halo_outer"
            :color="aircraftEventColor(lastEventsByCamera[camera.number])"
            :fill="true"
            :fill-color="aircraftEventColor(lastEventsByCamera[camera.number])"
            :fill-opacity="0.08"
            :interactive="false"
            :lat-lng="cameraLatLng(camera)"
            :opacity="0.95"
            pane="overlayPane"
            :radius="34"
            :weight="3"
          />
          <v-circle-marker
            v-for="camera in flashingMapCameras"
            :key="`aircraft-event-halo-inner-${camera.number}`"
            :bubbling-mouse-events="false"
            class-name="aircraft-events-map__event-halo aircraft-events-map__event-halo_inner"
            :color="aircraftEventColor(lastEventsByCamera[camera.number])"
            :fill="true"
            :fill-color="aircraftEventColor(lastEventsByCamera[camera.number])"
            :fill-opacity="0.12"
            :interactive="false"
            :lat-lng="cameraLatLng(camera)"
            :opacity="1"
            pane="overlayPane"
            :radius="22"
            :weight="3"
          />

          <v-marker
            v-for="camera in mapCameras"
            :key="camera.number"
            :ref="markerRef(camera.number)"
            :icon="getMarkerIcon(camera.number)"
            :lat-lng="cameraLatLng(camera)"
            :z-index-offset="isCameraFlashing(camera.number) ? eventMarkerZIndex : 0"
            @click="onCameraMarkerClick(camera, $event)"
            @popupclose="onCameraPopupClose(camera.number)"
            @popupopen="onCameraPopupOpen(camera)"
          >
            <!-- Leaflet переносит DOM tooltip/popup; постоянные обёртки остаются под управлением Vue. -->
            <div key="event-tooltip">
              <v-tooltip
                v-if="isCameraFlashing(camera.number) && !isMapSelectionMode"
                :options="eventTooltipOptions"
                @hook:beforeDestroy="unbindCameraTooltip(camera.number)"
              >
                <button
                  class="aircraft-events-map__event-label-content"
                  type="button"
                  :title="$t('eventVideo')"
                  @click.stop="onCameraMarkerClick(camera, $event)"
                >
                  <span
                    class="aircraft-events-map__event-label-address"
                    :title="camera.address || camera.title || $t('cameraLabel', {number: camera.number})"
                  >
                    {{ camera.address || camera.title || $t('cameraLabel', {number: camera.number}) }}
                  </span>
                  <AircraftEventTypeLabel :source-type="lastEventsByCamera[camera.number].source_type" />
                  <span
                    v-if="aircraftEventConfidence(lastEventsByCamera[camera.number])"
                    class="aircraft-events-map__event-confidence"
                  >
                    {{ aircraftEventConfidence(lastEventsByCamera[camera.number]) }}
                  </span>
                </button>
              </v-tooltip>
            </div>
            <div key="camera-popup">
              <v-popup
                v-if="!isMapSelectionMode && !isCameraFlashing(camera.number)"
                :options="cameraPopupOptions"
                @hook:beforeDestroy="unbindCameraPopup(camera.number)"
              >
                <div class="aircraft-events-map__popup">
                  <div class="aircraft-events-map__popup-preview">
                    <template
                      v-if="isCameraPopupOpen(camera.number)
                        && cameraPreviewUrl(camera)
                        && !isCameraPreviewFailed(camera.number)"
                    >
                      <img
                        v-if="hasStaticCameraPreview(camera)"
                        :alt="camera.title || String(camera.number)"
                        :src="cameraPreviewUrl(camera)"
                        @error="markCameraPreviewFailed(camera.number)"
                      >
                      <video
                        v-else
                        autoplay
                        loop
                        muted
                        playsinline
                        :src="cameraPreviewUrl(camera)"
                        @error="markCameraPreviewFailed(camera.number)"
                      />
                    </template>
                    <div
                      v-else-if="isCameraPopupOpen(camera.number)"
                      class="aircraft-events-map__popup-preview-empty"
                    >
                      {{ $t('cameraPreviewUnavailable') }}
                    </div>
                    <router-link
                      :aria-label="$t('video')"
                      class="aircraft-events-map__popup-play"
                      :title="$t('video')"
                      :to="cameraPlayerRoute(camera.number)"
                      @click.native.stop
                    >
                      <svg aria-hidden="true">
                        <use xlink:href="#icon-video-preview" />
                      </svg>
                    </router-link>
                  </div>
                  <div class="aircraft-events-map__popup-details">
                    <div class="aircraft-events-map__popup-title">
                      {{ camera.title || $t('cameraLabel', {number: camera.number}) }}
                    </div>
                    <div v-if="camera.address" class="aircraft-events-map__popup-address">
                      {{ camera.address }}
                    </div>
                    <div class="aircraft-events-map__popup-number">
                      {{ $t('cameraNumber') }} {{ camera.number }}
                    </div>
                    <div
                      v-if="lastEventsByCamera[camera.number]"
                      class="aircraft-events-map__popup-event"
                    >
                      <AircraftEventTypeLabel :source-type="lastEventsByCamera[camera.number].source_type" />
                      <span
                        v-if="aircraftEventConfidence(lastEventsByCamera[camera.number])"
                        class="aircraft-events-map__event-confidence"
                      >
                        {{ aircraftEventConfidence(lastEventsByCamera[camera.number]) }}
                      </span>
                      <span>
                        {{ $t('aircraftEvent') }} · {{ lastEventsByCamera[camera.number].date | localDateTime }}
                      </span>
                    </div>
                  </div>
                </div>
              </v-popup>
            </div>
          </v-marker>
        </v-map>

        <div class="aircraft-events-map__map-actions">
          <button
            v-if="mapCameras.length && !isMapInOverview"
            :aria-label="$t('mapOverview')"
            class="aircraft-events-map__overview-button"
            :title="$t('mapOverview')"
            type="button"
            @click="showMapOverview"
          >
            <svg aria-hidden="true" viewBox="0 0 32 32">
              <path d="M3 13.5L14.5 4L26 13.5" />
              <path d="M6.5 11.5V27H12.5V20H17.5V27H19" />
              <path d="M28.5 19.5C28.5 25.2 22.5 30 22.5 30C22.5 30 16.5 25.2 16.5 19.5C16.5 16.2 19.2 13.5 22.5 13.5C25.8 13.5 28.5 16.2 28.5 19.5Z" />
              <circle cx="22.5" cy="19.5" r="2.5" />
            </svg>
          </button>
          <button
            v-if="isEventsBarCollapsed"
            :aria-label="$t('sound')"
            :aria-pressed="isSoundNotificationsEnabled"
            class="aircraft-events-map__sound-button"
            :class="{'aircraft-events-map__sound-button_active': isSoundNotificationsEnabled}"
            :title="soundNotificationsButtonTitle"
            type="button"
            @click="toggleSoundNotifications"
          >
            <svg v-if="isSoundNotificationsEnabled" aria-hidden="true" viewBox="0 0 24 24">
              <path d="M11 5L6 9H3V15H6L11 19V5Z" />
              <path d="M15 9C15.8 9.8 16.2 10.8 16.2 12C16.2 13.2 15.8 14.2 15 15" />
              <path d="M18 6.5C19.5 8 20.2 9.8 20.2 12C20.2 14.2 19.5 16 18 17.5" />
            </svg>
            <svg v-else aria-hidden="true" viewBox="0 0 24 24">
              <path d="M11 5L6 9H3V15H6L11 19V5Z" />
              <path d="M16 9L21 14" />
              <path d="M21 9L16 14" />
            </svg>
          </button>
          <button
            v-if="isEventsBarCollapsed"
            :aria-label="$t('expandEventFeed')"
            class="aircraft-events-map__expand-events-button"
            :title="$t('expandEventFeed')"
            type="button"
            @click="setEventsBarCollapsed(false)"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M15 18L9 12L15 6" />
            </svg>
          </button>
          <button
            class="aircraft-events-map__map-action"
            :disabled="isSourceDialogOpen || isSavingEventSource || isSavingMapSelection"
            type="button"
            @click="openEventSourceDialog"
          >
            {{ $t('cameraLists') }}
          </button>
          <button
            class="aircraft-events-map__map-action"
            :class="{'aircraft-events-map__map-action_active': isMapSelectionMode}"
            :aria-pressed="isMapSelectionMode"
            :disabled="!sourceMosaicId || isSourceDialogOpen || isSavingEventSource || isSavingMapSelection || isLoading || !mapCameras.length"
            type="button"
            @click="isMapSelectionMode ? cancelMapSelection() : startMapSelection()"
          >
            {{ $t('selectEventCamerasOnMap') }}
          </button>
          <button
            :aria-label="fullscreenButtonTitle"
            :aria-pressed="isFullscreen"
            class="aircraft-events-map__fullscreen-button"
            :title="fullscreenButtonTitle"
            type="button"
            @click="toggleFullscreen"
          >
            <svg v-if="isFullscreen" aria-hidden="true" viewBox="0 0 24 24">
              <path d="M9 3V9H3" />
              <path d="M15 3V9H21" />
              <path d="M21 15H15V21" />
              <path d="M3 15H9V21" />
            </svg>
            <svg v-else aria-hidden="true" viewBox="0 0 24 24">
              <path d="M8 3H3V8" />
              <path d="M16 3H21V8" />
              <path d="M21 16V21H16" />
              <path d="M8 21H3V16" />
            </svg>
          </button>
        </div>

        <div v-if="isLoading" class="aircraft-events-map__overlay">
          <SpinnerLoading color="blue" />
        </div>
        <div v-else-if="mapCameras.length === 0" class="aircraft-events-map__overlay aircraft-events-map__empty">
          {{ $t('noCamerasFound') }}
        </div>
      </div>

      <div
        v-if="!eventsOnly && !isEventsBarCollapsed"
        aria-hidden="true"
        class="aircraft-events-map__events-resizer"
        :class="{'aircraft-events-map__events-resizer_active': isEventsBarResizing}"
        :title="$t('resizeEventFeed')"
        @pointerdown.prevent="startEventsBarResize"
      />

      <aside
        v-if="eventsOnly || !isEventsBarCollapsed"
        class="aircraft-events-map__events-bar"
        :class="{'aircraft-events-map__events-bar_standalone': eventsOnly}"
      >
        <div class="aircraft-events-map__events-header">
          <div class="aircraft-events-map__events-heading">
            <span>{{ $t('events') }}</span>
            <span
              v-if="sourceMosaicId"
              class="aircraft-events-map__source-title"
              :title="String(sourceMosaicTitle || sourceMosaicId)"
            >
              {{ sourceMosaicTitle || sourceMosaicId }}
            </span>
          </div>
          <div class="aircraft-events-map__events-actions">
            <CamsButton
              class="aircraft-events-map__events-action aircraft-events-map__events-action_text"
              :disabled="isLoading || isLoadingEventSource || isSavingEventSource || isSavingMapSelection"
              type="button"
              @click="openAircraftReport"
            >
              {{ $t('report') }}
            </CamsButton>
            <button
              v-if="eventsOnly"
              :disabled="isSourceDialogOpen || isSavingEventSource || isSavingMapSelection"
              class="aircraft-events-map__events-action aircraft-events-map__events-action_text"
              type="button"
              @click="openEventSourceDialog"
            >
              {{ $t('cameraLists') }}
            </button>
            <button
              :aria-label="$t('sound')"
              :aria-pressed="isSoundNotificationsEnabled"
              class="aircraft-events-map__events-action"
              :class="{'aircraft-events-map__events-action_active': isSoundNotificationsEnabled}"
              :title="soundNotificationsButtonTitle"
              type="button"
              @click="toggleSoundNotifications"
            >
              <svg v-if="isSoundNotificationsEnabled" aria-hidden="true" viewBox="0 0 24 24">
                <path d="M11 5L6 9H3V15H6L11 19V5Z" />
                <path d="M15 9C15.8 9.8 16.2 10.8 16.2 12C16.2 13.2 15.8 14.2 15 15" />
                <path d="M18 6.5C19.5 8 20.2 9.8 20.2 12C20.2 14.2 19.5 16 18 17.5" />
              </svg>
              <svg v-else aria-hidden="true" viewBox="0 0 24 24">
                <path d="M11 5L6 9H3V15H6L11 19V5Z" />
                <path d="M16 9L21 14" />
                <path d="M21 9L16 14" />
              </svg>
            </button>
            <router-link
              v-if="!eventsOnly"
              :aria-label="$t('openEventFeedInNewWindow')"
              class="aircraft-events-map__events-action"
              rel="noopener noreferrer"
              target="_blank"
              :title="$t('openEventFeedInNewWindow')"
              :to="{name: routes.ROUTE_PACS_AIRCRAFT_LIVE_EVENTS}"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M14 3H21V10" />
                <path d="M10 14L21 3" />
                <path d="M21 14V21H3V3H10" />
              </svg>
            </router-link>
            <button
              v-if="!eventsOnly"
              :aria-label="$t('collapseEventFeed')"
              class="aircraft-events-map__events-action"
              :title="$t('collapseEventFeed')"
              :disabled="isMapSelectionMode"
              type="button"
              @click="setEventsBarCollapsed(true)"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M9 18L15 12L9 6" />
              </svg>
            </button>
          </div>
        </div>

        <div v-if="isMapSelectionMode" class="aircraft-events-map__source">
          <div class="aircraft-events-map__map-selection">
            <p>{{ $t('drawEventCameraArea') }}</p>
            <p>{{ $t('camerasInSelectedArea', {count: camerasInSelectedArea.length}) }}</p>
            <p v-if="mapSelectionError" role="alert">
              {{ $t(mapSelectionError) }}
            </p>
            <div class="aircraft-events-map__source-actions">
              <CamsButton
                type="button"
                :disabled="!mapSelectionPoints.length || isSavingMapSelection"
                @click="mapSelectionPoints.pop()"
              >
                {{ $t('undoAreaPoint') }}
              </CamsButton>
              <CamsButton type="button" :disabled="isSavingMapSelection" @click="cancelMapSelection()">
                {{ $t('cancel') }}
              </CamsButton>
              <CamsButton
                type="button"
                priority="primary"
                :disabled="!camerasInSelectedArea.length || isSavingMapSelection"
                @click="saveMapSelection"
              >
                {{ $t('save') }}
              </CamsButton>
            </div>
            <SpinnerLoading v-if="isSavingMapSelection" color="blue" />
          </div>
        </div>

        <div v-if="isLoadingEventSource" class="aircraft-events-map__events-empty">
          <SpinnerLoading color="blue" />
        </div>
        <div v-else-if="!sourceMosaicId" class="aircraft-events-map__events-empty">
          <div class="aircraft-events-map__source-setup">
            <p v-if="eventSourceErrorKey">
              {{ $t(eventSourceErrorKey) }}
            </p>
            <CamsButton
              type="button"
              class="aircraft-events-map__source-setup-button"
              :disabled="isSourceDialogOpen || isSavingEventSource"
              @click="openEventSourceDialog"
            >
              {{ $t('setMosaicEventSource') }}
            </CamsButton>
          </div>
        </div>
        <div v-else-if="liveEvents.length === 0" class="aircraft-events-map__events-empty">
          {{ $t(eventSourceErrorKey || 'noEvents') }}
        </div>

        <div v-else class="aircraft-events-map__events-list">
          <p v-if="eventSourceErrorKey" class="aircraft-events-map__source-error">
            {{ $t(eventSourceErrorKey) }}
          </p>
          <article
            v-for="eventItem in liveEvents"
            :key="eventItem.key"
            class="aircraft-events-map__event"
            :class="{'aircraft-events-map__event_active': eventItem.key === activeEventKey}"
          >
            <button
              class="aircraft-events-map__event-info"
              type="button"
              @click="selectEvent(eventItem)"
            >
              <span class="aircraft-events-map__event-title">
                {{ eventItem.camera.title || $t('cameraLabel', {number: eventItem.cameraNumber}) }}
              </span>
              <span class="aircraft-events-map__event-number">
                {{ $t('cameraNumber') }} {{ eventItem.cameraNumber }}
              </span>
              <span v-if="eventItem.message.text" class="aircraft-events-map__event-text">
                {{ eventItem.message.text }}
              </span>
              <AircraftEventTypeLabel :source-type="eventItem.message.source_type" />
              <span
                v-if="aircraftEventConfidence(eventItem.message)"
                class="aircraft-events-map__event-confidence"
              >
                {{ aircraftEventConfidence(eventItem.message) }}
              </span>
              <span class="aircraft-events-map__event-time">
                {{ eventItem.message.date | localDateTime }}
              </span>
            </button>
            <router-link
              :aria-label="$t('eventVideo')"
              class="aircraft-events-map__event-play"
              :title="$t('eventVideo')"
              :to="eventPlayerRoute(eventItem)"
            >
              <svg aria-hidden="true">
                <use xlink:href="#icon-video-preview" />
              </svg>
            </router-link>
          </article>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import {mapState} from "vuex";
import {
  LCircleMarker,
  LControlAttribution,
  LControlZoom,
  LMap,
  LMarker,
  LPopup,
  LPolygon,
  LPolyline,
  LTileLayer,
  LTooltip,
} from "vue2-leaflet";
import {
  ACTION_LOAD_CAMERAS,
  CAMERA_SCREENSHOT_SIZES,
  FIELDS_CAMERA,
} from "@/store/cameras/index.js";
import {
  ACTION_LOAD_AIRCRAFT_ANALYTICS,
  CAMERA_AIRCRAFT_ANALYTICS_STRUCT,
} from "@/store/analytics/index.js";
import {ACTION_GET_ANALYTIC_SSE_URL, ACTION_LOAD_MOSAIC, ACTION_SAVE_MOSAIC} from "@/store/mosaics/index.js";
import MosaicEventSourceDialog from "@/components/pacs/analytics/aircraft/MosaicEventSourceDialog.vue";
import AircraftEventTypeLabel from "@/components/pacs/analytics/aircraft/AircraftEventTypeLabel.vue";
import {isPointInPolygon} from "@/components/pacs/analytics/aircraft/mapSelection.js";
import {aircraftEventColor, cameraMarkerIcons, selectionPointIcon} from "@/components/pacs/analytics/aircraft/aircraftMapMarkers.js";
import {ANALYTICS} from "@/store/analytics/helpers.js";
import SSEWorker from "@/store/analytics/sse.js";
import {messageSoundNotificationMixin} from "@/components/events/mixins.js";
import {aircraftEventFiltersMixin} from "@/components/events/aircraftEventFilters.js";
import {DEFAULT_MULTI_SSE_TTL, MAP_TILES_URL, PAGE_SIZES, TOKEN_TTL} from "@/utils/consts.js";
import {
  ROUTE_PACS_AIRCRAFT_ANALYTICS,
  ROUTE_PACS_AIRCRAFT_CAMERAS_REPORT,
  ROUTE_PACS_AIRCRAFT_LIVE_EVENTS,
} from "@/router/names.js";
import {
  QUERY_KEY_AIRCRAFT_REPORT_CAMERA_NUMBERS,
  QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER,
  QUERY_KEY_ONE_SCREEN_TAB,
  QUERY_KEY_ONE_SCREEN_TIME_SHIFT,
} from "@/router/queryKeys.js";
import {exitFullscreen, requestFullscreenByElement} from "camsng-frontend-shared/lib/helpers.js";

const EVENT_FOCUS_ZOOM = 15;
const EVENTS_LIMIT = 100;
const EVENT_PLAYBACK_LEAD_MS = 8000;
const MARKER_FLASH_DURATION = 60000;
const CAMERA_WARNING_EVENT_THRESHOLD = 5;
const CAMERA_ALARM_EVENT_THRESHOLD = 10;
const CAMERA_NUMBER_FIELD = CAMERA_AIRCRAFT_ANALYTICS_STRUCT.fields.camera_number.name;
const EVENT_SOURCE_CLOSED = 2;
const SSE_RECONNECT_DELAY = 10000;
const SSE_RETRY_LOAD_DELAY = 60000;
const SOUND_NOTIFICATIONS_STORAGE_KEY = "camsng:aircraft-events:sound-notifications-enabled";
const MOSAIC_EVENT_SOURCE_STORAGE_KEY = "camsng:aircraft-events:mosaic-source";
const EVENT_MARKER_Z_INDEX = 1000;
const EVENTS_BAR_DEFAULT_WIDTH = 320;
const EVENTS_BAR_MIN_WIDTH = 260;
const EVENTS_BAR_MAX_WIDTH = 720;
const EVENTS_BAR_RESIZER_WIDTH = 14;
const MAP_MIN_WIDTH = 360;
const STACKED_LAYOUT_MEDIA_QUERY = "(max-width: 900px)";
const EVENT_TOOLTIP_OPTIONS = Object.freeze({
  className: "aircraft-events-map__event-label",
  direction: "top",
  interactive: true,
  offset: [0, -51],
  opacity: 1,
  permanent: true,
});
const CAMERA_POPUP_OPTIONS = Object.freeze({
  autoPanPadding: [24, 24],
  className: "aircraft-events-map__leaflet-popup",
  maxWidth: 256,
  minWidth: 256,
});

/**
 * Карта камер с аналитикой летательных аппаратов.
 * Показывает все камеры aircraft, выделяет тревожные камеры выбранной мозаики
 * и получает события через её общий SSE-канал.
 * Новые события попадают в ленту, при включённых оповещениях сопровождаются звуком
 * и временно выделяют камеру-источник.
 */
export default {
  name: "AircraftEventsMap",
  mixins: [messageSoundNotificationMixin, aircraftEventFiltersMixin],
  components: {
    AircraftEventTypeLabel,
    "v-circle-marker": LCircleMarker,
    "v-control-attribution": LControlAttribution,
    "v-control-zoom": LControlZoom,
    "v-map": LMap,
    "v-marker": LMarker,
    "v-popup": LPopup,
    "v-polygon": LPolygon,
    "v-polyline": LPolyline,
    "v-tile-layer": LTileLayer,
    "v-tooltip": LTooltip,
  },
  props: {
    /** Отображает только ленту событий в отдельном окне без карты и навигации. */
    eventsOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      routes: {
        ROUTE_PACS_AIRCRAFT_ANALYTICS,
        ROUTE_PACS_AIRCRAFT_LIVE_EVENTS,
      },
      mapTilesUrl: MAP_TILES_URL,
      mapOptions: {zoomControl: false, attributionControl: false},
      cameraPopupOptions: CAMERA_POPUP_OPTIONS,
      eventMarkerZIndex: EVENT_MARKER_Z_INDEX,
      eventTooltipOptions: EVENT_TOOLTIP_OPTIONS,
      selectionPointIcon,
      bounds: null,
      cameras: [],
      camerasByNumber: {},
      registeredWorker: null,
      sseUrl: null,
      sourceMosaicId: null,
      sourceMosaicTitle: "",
      sourceAlarmCameraNumbers: [],
      sourceRequestVersion: 0,
      isSourceDialogOpen: false,
      isSavingEventSource: false,
      isMapSelectionMode: false,
      isSavingMapSelection: false,
      mapSelectionPoints: [],
      mapSelectionError: null,
      restoreDoubleClickZoom: false,
      isLoadingEventSource: false,
      eventSourceErrorKey: null,
      lastEventsByCamera: {},
      eventCountsByCamera: {},
      liveEvents: [],
      openPopupCameraNumber: null,
      popupPreviewSigns: {},
      popupPreviewErrors: {},
      eventSequence: 0,
      isSoundNotificationsEnabled: true,
      activeEventKey: null,
      activeCameraNumber: null,
      flashingCameraNumbers: {},
      isEventsBarCollapsed: false,
      isEventsBarResizing: false,
      eventsBarWidth: EVENTS_BAR_DEFAULT_WIDTH,
      eventsBarResizeStartX: 0,
      eventsBarResizeStartWidth: EVENTS_BAR_DEFAULT_WIDTH,
      eventsBarResizePointerId: null,
      mapResizeFrameId: null,
      updateOverviewAfterMapResize: false,
      isMapInOverview: true,
      isFullscreen: false,
      isLoading: true,
      refreshTimeoutId: null,
      reconnectTimeoutId: null,
      markerFlashTimeoutIds: {},
      focusTimeoutId: null,
      isComponentDestroyed: false,
    };
  },
  computed: {
    ...mapState({
      defaultCenter: state => state.map.defaultCenter,
      minZoom: state => state.map.minZoom,
      zoom: state => state.map.zoom,
    }),
    attributionLayer() {
      return this.$t('participants', {link: '<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'});
    },
    sourceCameraNumbers() {
      return new Set(this.sourceAlarmCameraNumbers.map(String));
    },
    camerasInSelectedArea() {
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (!map || this.mapSelectionPoints.length < 3) return [];
      const polygon = this.mapSelectionPoints.map(point => map.project(point, 0));
      return this.mapCameras.filter(camera => isPointInPolygon(map.project(this.cameraLatLng(camera), 0), polygon));
    },
    mapCameras() {
      return this.cameras.filter(this.hasValidCoordinates);
    },
    flashingMapCameras() {
      return this.mapCameras.filter(camera => this.isCameraFlashing(camera.number));
    },
    warningEventCountMapCameras() {
      return this.mapCameras.filter(camera => (
        Number(this.eventCountsByCamera[String(camera.number)] || 0) > CAMERA_WARNING_EVENT_THRESHOLD
      ));
    },
    alarmEventCountMapCameras() {
      return this.mapCameras.filter(camera => (
        Number(this.eventCountsByCamera[String(camera.number)] || 0) > CAMERA_ALARM_EVENT_THRESHOLD
      ));
    },
    soundNotificationsButtonTitle() {
      return this.$t(
        this.isSoundNotificationsEnabled ? 'disableSoundNotifications' : 'enableSoundNotifications'
      );
    },
    fullscreenButtonTitle() {
      return this.$t(this.isFullscreen ? 'exitFullscreen' : 'fullscreen');
    },
    eventsContentStyle() {
      return {
        "--events-bar-width": `${this.eventsBarWidth}px`,
      };
    },
  },
  mounted() {
    this.restoreSoundNotificationsPreference();
    this.restoreMosaicEventSource();
    window.addEventListener("storage", this.onSoundNotificationsStorageChange);
    window.addEventListener("storage", this.onMosaicEventSourceStorageChange);
    if (!this.eventsOnly) {
      const map = this.$refs.map.mapObject;
      map._controlCorners.verticalcenterright = L.DomUtil.create(
        "div",
        "leaflet-verticalcenter leaflet-right",
        map._controlContainer
      );
      window.addEventListener("resize", this.handleWindowResize);
      document.addEventListener("fullscreenchange", this.handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);
      this.$nextTick(this.handleWindowResize);
    }
    this.reloadCamerasAndConnections();
  },
  beforeDestroy() {
    this.isComponentDestroyed = true;
    this.sourceRequestVersion += 1;
    this.cancelMapSelection(true);
    if (this.getFullscreenElement() === this.getFullscreenTarget()) {
      const exitPromise = exitFullscreen();
      if (exitPromise && typeof exitPromise.catch === "function") {
        exitPromise.catch(error => {
          devLog("[AircraftEventsMap] Failed to exit fullscreen on destroy", error);
        });
      }
    }
    window.removeEventListener("storage", this.onSoundNotificationsStorageChange);
    window.removeEventListener("storage", this.onMosaicEventSourceStorageChange);
    window.removeEventListener("resize", this.handleWindowResize);
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);
    this.removeEventsBarResizeListeners();
    document.body.classList.remove("aircraft-events-map-events-resizing");
    if (this.mapResizeFrameId !== null) {
      window.cancelAnimationFrame(this.mapResizeFrameId);
      this.mapResizeFrameId = null;
    }
    clearTimeout(this.refreshTimeoutId);
    clearTimeout(this.focusTimeoutId);
    this.clearMarkerFlashTimeouts();
    this.closeWorker();
  },
  methods: {
    openAircraftReport() {
      const {href} = this.$router.resolve({
        name: ROUTE_PACS_AIRCRAFT_CAMERAS_REPORT,
        query: {
          [QUERY_KEY_AIRCRAFT_REPORT_CAMERA_NUMBERS]: this.sourceAlarmCameraNumbers.map(String),
        },
      });
      window.open(href, "_blank", "noopener,noreferrer");
    },
    // Маркеры, popup и превью камер.
    markerRef(cameraNumber) {
      return `aircraftMarker_${cameraNumber}`;
    },
    getCameraMarker(cameraNumber) {
      const markerRefs = this.$refs[this.markerRef(cameraNumber)];
      return Array.isArray(markerRefs) ? markerRefs[0] : markerRefs;
    },
    cameraLatLng(camera) {
      return [Number(camera.latitude), Number(camera.longitude)];
    },
    hasValidCoordinates(camera) {
      return camera.hasCoordinates
        && Number.isFinite(Number(camera.latitude))
        && Number.isFinite(Number(camera.longitude));
    },
    getMarkerIcon(cameraNumber) {
      if (this.isCameraFlashing(cameraNumber)) {
        const message = this.lastEventsByCamera[cameraNumber];
        if (message.source_type === "audio") return cameraMarkerIcons.audio;
        if (message.source_type === "video") return cameraMarkerIcons.video;
      }
      return this.sourceCameraNumbers.has(String(cameraNumber)) ? cameraMarkerIcons.selected : cameraMarkerIcons.default;
    },
    aircraftEventColor,
    aircraftEventConfidence(message) {
      if (!message) return "";
      const confidence = message.confidence;
      const hasConfidence = (typeof confidence === "number" || (typeof confidence === "string" && confidence.trim() !== ""))
        && Number.isFinite(Number(confidence)) && Number(confidence) >= 0 && Number(confidence) <= 100;
      return hasConfidence ? this.$t('aircraftEventConfidence', {value: Number(confidence)}) : "";
    },
    onCameraMarkerClick(camera, event) {
      if (this.isMapSelectionMode) {
        this.addMapSelectionPoint(event);
        return;
      }
      const message = this.lastEventsByCamera[camera.number];
      if (this.isCameraFlashing(camera.number) && message) {
        const route = this.eventPlayerRoute({cameraNumber: camera.number, message});
        if (this.$router.resolve(route).route.fullPath !== this.$route.fullPath) {
          this.$router.push(route);
        }
      }
    },
    isCameraFlashing(cameraNumber) {
      return Boolean(this.flashingCameraNumbers[String(cameraNumber)]);
    },
    isCameraPopupOpen(cameraNumber) {
      return this.openPopupCameraNumber === String(cameraNumber);
    },
    hasStaticCameraPreview(camera) {
      return Boolean(camera.server && camera.server.screenshot_domain);
    },
    cameraPreviewUrl(camera) {
      if (!camera || typeof camera.screenshotUrl !== "function") return "";
      return camera.screenshotUrl(
        CAMERA_SCREENSHOT_SIZES.s400,
        this.popupPreviewSigns[String(camera.number)]
      );
    },
    isCameraPreviewFailed(cameraNumber) {
      return Boolean(this.popupPreviewErrors[String(cameraNumber)]);
    },
    markCameraPreviewFailed(cameraNumber) {
      this.$set(this.popupPreviewErrors, String(cameraNumber), true);
    },
    onCameraPopupOpen(camera) {
      const cameraNumber = String(camera.number);
      this.openPopupCameraNumber = cameraNumber;
      this.$delete(this.popupPreviewErrors, cameraNumber);
      // Новый параметр сбрасывает браузерный кэш превью при каждом открытии popup.
      this.$set(this.popupPreviewSigns, cameraNumber, `${Date.now()}`);
    },
    onCameraPopupClose(cameraNumber) {
      if (this.openPopupCameraNumber === String(cameraNumber)) {
        this.openPopupCameraNumber = null;
      }
    },
    unbindCameraPopup(cameraNumber) {
      const marker = this.getCameraMarker(cameraNumber);
      marker && marker.mapObject && marker.mapObject.unbindPopup();
    },
    /**
     * Формирует маршрут стандартного live-плеера камеры.
     * Удаляет время архива и выбранную вкладку SmartPlayer, остальные query-параметры сохраняет.
     */
    cameraPlayerRoute(cameraNumber) {
      const query = {...(this.$route.query || {})};
      delete query[QUERY_KEY_ONE_SCREEN_TIME_SHIFT];
      delete query[QUERY_KEY_ONE_SCREEN_TAB];
      query[QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER] = String(cameraNumber);
      return {name: this.$route.name, query};
    },
    // Выбор и сохранение источника событий.

    openEventSourceDialog() {
      if (this.isSourceDialogOpen || this.isSavingEventSource || this.isSavingMapSelection || this.isComponentDestroyed) return;
      this.cancelMapSelection();
      this.isSourceDialogOpen = true;
      this.$camsdals.open(
        MosaicEventSourceDialog,
        {
          initialMosaicId: this.sourceMosaicId,
          onEventFiltersChange: (filters) => {
            if (this.isComponentDestroyed) return;
            this.aircraftAudioEventsEnabled = filters.aircraftAudioEventsEnabled;
            this.aircraftVideoEventsEnabled = filters.aircraftVideoEventsEnabled;
          },
          onSavingChange: (isSaving) => {
            if (!this.isComponentDestroyed) this.isSavingEventSource = isSaving;
          },
          onSourceSaved: (mosaic) => {
            if (this.isComponentDestroyed) return;
            this.setMosaicEventSource(mosaic.id, mosaic);
            this.saveMosaicEventSource();
          },
        },
        {dialogTitle: this.$t('chooseEventSourceMosaic')},
        {
          size: "vuedal-auto-width vuedal-all-height",
          name: "js-click-outside",
          onDismiss: () => {
            this.isSourceDialogOpen = false;
          },
          onClose: () => {
            this.isSourceDialogOpen = false;
          },
        }
      );
    },
    readMosaicEventSource(rawValue) {
      try {
        const source = JSON.parse(rawValue);
        const mosaicId = Number(source && source.mosaicId);
        return Number.isInteger(mosaicId) && mosaicId > 0 ? mosaicId : null;
      } catch {
        return null;
      }
    },
    restoreMosaicEventSource() {
      try {
        this.sourceMosaicId = this.readMosaicEventSource(window.localStorage.getItem(MOSAIC_EVENT_SOURCE_STORAGE_KEY));
      } catch (error) {
        devLog("[AircraftEventsMap] Failed to restore mosaic event source", error);
      }
    },
    saveMosaicEventSource() {
      try {
        if (this.sourceMosaicId) {
          // Метка обновления извещает отдельное окно ленты и при изменении камер той же мозаики.
          window.localStorage.setItem(MOSAIC_EVENT_SOURCE_STORAGE_KEY, JSON.stringify({
            mosaicId: this.sourceMosaicId,
            updatedAt: Date.now(),
          }));
        } else {
          window.localStorage.removeItem(MOSAIC_EVENT_SOURCE_STORAGE_KEY);
        }
      } catch (error) {
        devLog("[AircraftEventsMap] Failed to store mosaic event source", error);
      }
    },
    onMosaicEventSourceStorageChange(event) {
      if (event.storageArea !== window.localStorage || (event.key !== MOSAIC_EVENT_SOURCE_STORAGE_KEY && event.key !== null)) return;
      this.setMosaicEventSource(this.readMosaicEventSource(event.newValue));
    },
    setMosaicEventSource(mosaicId, mosaic = null) {
      this.cancelMapSelection(true);
      this.sourceRequestVersion += 1;
      clearTimeout(this.refreshTimeoutId);
      this.closeWorker();
      this.resetEventFeed();
      this.sourceMosaicId = mosaicId;
      this.sourceMosaicTitle = mosaic ? mosaic.title : "";
      this.sourceAlarmCameraNumbers = mosaic ? [...mosaic.alarmCameraNumbers] : [];
      this.eventSourceErrorKey = null;
      return this.reloadCamerasAndConnections();
    },
    resetEventFeed() {
      clearTimeout(this.focusTimeoutId);
      this.clearMarkerFlashTimeouts();
      this.stopEventNotificationSound();
      this.liveEvents = [];
      this.lastEventsByCamera = {};
      this.eventCountsByCamera = {};
      this.activeEventKey = null;
      this.activeCameraNumber = null;
      this.openPopupCameraNumber = null;
    },
    isCurrentSourceRequest(version) {
      return !this.isComponentDestroyed && this.sourceRequestVersion === version;
    },
    // Выбор тревожных камер многоугольником на карте.
    startMapSelection() {
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (!map || !this.sourceMosaicId || this.isSavingMapSelection || this.isSavingEventSource) return;
      this.cancelMapSelection();
      this.setEventsBarCollapsed(false);
      this.isMapSelectionMode = true;
      this.restoreDoubleClickZoom = map.doubleClickZoom.enabled();
      map.doubleClickZoom.disable();
      map.closePopup();
      map.getContainer().classList.add("aircraft-events-map_selecting");
    },
    addMapSelectionPoint(event) {
      if (!this.isMapSelectionMode || this.isSavingMapSelection || !event.latlng) return;
      if (event.originalEvent && event.originalEvent.detail > 1) return;
      const point = [event.latlng.lat, event.latlng.lng];
      const previous = this.mapSelectionPoints[this.mapSelectionPoints.length - 1];
      if (previous && previous[0] === point[0] && previous[1] === point[1]) return;
      this.mapSelectionPoints.push(point);
      this.mapSelectionError = null;
    },
    moveMapSelectionPoint(index, event) {
      if (!this.isMapSelectionMode || this.isSavingMapSelection || !this.mapSelectionPoints[index]) return;
      const point = event.target.getLatLng();
      this.$set(this.mapSelectionPoints, index, [point.lat, point.lng]);
      this.mapSelectionError = null;
    },
    stopMapSelectionPointClick(event) {
      if (event.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
    },
    cancelMapSelection(force = false) {
      if (this.isSavingMapSelection && !force) return;
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (map) {
        if (this.restoreDoubleClickZoom) map.doubleClickZoom.enable();
        map.getContainer().classList.remove("aircraft-events-map_selecting");
      }
      this.restoreDoubleClickZoom = false;
      this.isMapSelectionMode = false;
      this.mapSelectionPoints = [];
      this.mapSelectionError = null;
    },
    async saveMapSelection() {
      if (!this.isMapSelectionMode || this.isSavingMapSelection || !this.sourceMosaicId || !this.camerasInSelectedArea.length) return;
      const mosaicId = this.sourceMosaicId;
      const alarmCameraNumbers = this.camerasInSelectedArea.map(camera => camera.number);
      this.isSavingMapSelection = true;
      this.mapSelectionError = null;
      try {
        const mosaic = await this.$store.dispatch(`mosaics/${ACTION_LOAD_MOSAIC}`, mosaicId);
        if (this.isComponentDestroyed || this.sourceMosaicId !== mosaicId || !this.isMapSelectionMode) return;
        if (!mosaic) {
          this.mapSelectionError = "mosaicEventSourceUnavailable";
          return;
        }
        await this.$store.dispatch(`mosaics/${ACTION_SAVE_MOSAIC}`, {
          mosaicId: mosaic.id,
          mosaicTitle: mosaic.title,
          mosaicGrid: mosaic.grid,
          viewCameraNumbers: mosaic.viewCameraNumbers,
          lowLatencyMode: mosaic.lowLatencyMode,
          alarmCameraNumbers,
          alarmSlots: mosaic.alarmSlots,
        });
        if (this.isComponentDestroyed || this.sourceMosaicId !== mosaicId) return;
        mosaic.alarmCameraNumbers = alarmCameraNumbers;
        const refresh = this.setMosaicEventSource(mosaic.id, mosaic);
        this.saveMosaicEventSource();
        await refresh;
      } catch {
        if (!this.isComponentDestroyed && this.sourceMosaicId === mosaicId) {
          this.mapSelectionError = "saveMosaicEventSourceError";
        }
      } finally {
        if (!this.isComponentDestroyed) this.isSavingMapSelection = false;
      }
    },
    // Загрузка камер и периодическое обновление данных.

    /** Загружает все страницы aircraft-аналитики и возвращает уникальные номера камер. */
    async loadAircraftCameraNumbers() {
      const cameraNumbers = new Set();
      let page = 1;
      let pageCount = 1;

      while (page <= pageCount) {
        const response = await this.$store.dispatch(`analytics/${ACTION_LOAD_AIRCRAFT_ANALYTICS}`, {
          fields: [CAMERA_NUMBER_FIELD],
          filters: [],
          orderBy: [],
          page,
          pageSize: PAGE_SIZES.DEFAULT,
          search: "",
        });

        if (this.isComponentDestroyed) return [];

        for (const rawAircraftInfo of response.results || []) {
          const cameraNumber = rawAircraftInfo[CAMERA_NUMBER_FIELD];
          if (cameraNumber || cameraNumber === 0) {
            cameraNumbers.add(cameraNumber);
          }
        }
        pageCount = response.page && response.page.all || 0;
        page += 1;
      }

      return Array.from(cameraNumbers);
    },
    /** Загружает координаты, подписи, серверы и live-токены найденных камер. */
    async loadAircraftCameras(cameraNumbers) {
      if (cameraNumbers.length === 0) return [];

      return this.$store.dispatch(`cameras/${ACTION_LOAD_CAMERAS}`, {
        fields: [
          FIELDS_CAMERA.number,
          FIELDS_CAMERA.address,
          FIELDS_CAMERA.title,
          FIELDS_CAMERA.latitude,
          FIELDS_CAMERA.longitude,
          FIELDS_CAMERA.server,
          FIELDS_CAMERA.token_l,
        ],
        numbers: cameraNumbers,
        tokenLiveTTL: TOKEN_TTL.MULTI,
      });
    },
    /**
     * Обновляет все камеры карты и настройки выбранной мозаики, затем её общий SSE-канал.
     * Версия запроса защищает от завершения старой загрузки после смены источника или ухода со страницы.
     */
    async reloadCamerasAndConnections() {
      const version = ++this.sourceRequestVersion;
      clearTimeout(this.refreshTimeoutId);
      clearTimeout(this.reconnectTimeoutId);
      this.isLoading = this.cameras.length === 0;
      this.isLoadingEventSource = Boolean(this.sourceMosaicId) && !this.registeredWorker;
      let refreshDelay = SSE_RETRY_LOAD_DELAY;

      try {
        const [cameraNumbers, mosaic] = await Promise.all([
          this.loadAircraftCameraNumbers(),
          this.sourceMosaicId
            ? this.$store.dispatch(`mosaics/${ACTION_LOAD_MOSAIC}`, this.sourceMosaicId)
            : Promise.resolve(null),
        ]);
        if (!this.isCurrentSourceRequest(version)) return;

        if (this.sourceMosaicId && !mosaic) {
          this.closeWorker();
          this.resetEventFeed();
          this.sourceMosaicId = null;
          this.sourceMosaicTitle = "";
          this.sourceAlarmCameraNumbers = [];
          this.eventSourceErrorKey = "mosaicEventSourceUnavailable";
          this.saveMosaicEventSource();
        } else if (mosaic) {
          const previousNumbers = this.sourceCameraNumbers;
          const nextNumbers = new Set(mosaic.alarmCameraNumbers.map(String));
          if (previousNumbers.size !== nextNumbers.size || [...nextNumbers].some(number => !previousNumbers.has(number))) {
            this.resetEventFeed();
          }
          this.sourceMosaicTitle = mosaic.title;
          this.sourceAlarmCameraNumbers = [...mosaic.alarmCameraNumbers];
        }

        const cameras = await this.loadAircraftCameras(cameraNumbers);
        if (!this.isCurrentSourceRequest(version)) return;
        const camerasByNumber = cameras.reduce((result, camera) => {
          result[String(camera.number)] = camera;
          return result;
        }, {});
        this.camerasByNumber = camerasByNumber;
        this.cameras = Object.values(camerasByNumber);
        this.updateBounds();
        await this.$nextTick();
        if (!this.isCurrentSourceRequest(version)) return;
        if (this.sourceMosaicId) {
          await this.setupEvents(version);
        }
        refreshDelay = this.registeredWorker || !this.sourceMosaicId ? DEFAULT_MULTI_SSE_TTL : SSE_RETRY_LOAD_DELAY;
      } catch (error) {
        if (this.isCurrentSourceRequest(version) && this.sourceMosaicId) {
          this.eventSourceErrorKey = "loadMosaicEventSourceError";
        }
        devLog("[AircraftEventsMap] Failed to load cameras or mosaic event source", error);
      } finally {
        if (this.isCurrentSourceRequest(version)) {
          this.isLoading = false;
          this.isLoadingEventSource = false;
          this.refreshTimeoutId = setTimeout(this.reloadCamerasAndConnections, refreshDelay);
        }
      }
    },
    /** Пересчитывает общие границы карты по камерам с корректными координатами. */
    updateBounds() {
      const bounds = L.latLngBounds(this.mapCameras.map(this.cameraLatLng));
      this.bounds = bounds.isValid() ? bounds.pad(0.05) : null;
    },
    // Один SSE-канал мозаики: тот же источник URL, который используется в MultiScreen.setupEvents.
    async setupEvents(version) {
      const sseUrl = await this.$store.dispatch(`mosaics/${ACTION_GET_ANALYTIC_SSE_URL}`, this.sourceMosaicId);
      if (!this.isCurrentSourceRequest(version)) return;
      this.closeWorker();
      this.sseUrl = sseUrl;
      if (!sseUrl) {
        this.eventSourceErrorKey = "mosaicEventSourceConnectionError";
        return;
      }
      this.openWorker(sseUrl);
    },
    /**
     * Открывает общий SSE мозаики и регистрирует обработчики соединения.
     * EventSource сам восстанавливает CONNECTING, а CLOSED переподнимается вручную.
     */
    openWorker(sseUrl) {
      const mosaicId = this.sourceMosaicId;
      try {
        const worker = new SSEWorker(sseUrl, (analyticMessage) => {
          if (this.isComponentDestroyed || this.registeredWorker !== worker || this.sourceMosaicId !== mosaicId) return;
          this.onNewMessage(analyticMessage);
        });
        this.registeredWorker = worker;
        this.eventSourceErrorKey = null;
        worker.registeredHandlers.open = () => {
          if (this.registeredWorker !== worker) return;
          clearTimeout(this.reconnectTimeoutId);
          this.eventSourceErrorKey = null;
        };
        worker.registeredHandlers.error = () => {
          if (this.registeredWorker !== worker) return;
          this.eventSourceErrorKey = "mosaicEventSourceConnectionError";
          if (worker.eventSource.readyState === EVENT_SOURCE_CLOSED) {
            this.scheduleWorkerReconnect(mosaicId, worker);
          }
        };
        worker.eventSource.addEventListener("open", worker.registeredHandlers.open);
        worker.eventSource.addEventListener("error", worker.registeredHandlers.error);
      } catch (error) {
        this.closeWorker();
        this.eventSourceErrorKey = "mosaicEventSourceConnectionError";
        this.scheduleWorkerReconnect(mosaicId, null);
        devLog("[AircraftEventsMap] Failed to open mosaic SSE", error);
      }
    },
    /**
     * Перезапрашивает URL после закрытия соединения, чтобы не использовать истёкший токен.
     */
    scheduleWorkerReconnect(mosaicId, expectedWorker) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = setTimeout(() => {
        if (this.isComponentDestroyed || this.sourceMosaicId !== mosaicId || this.registeredWorker !== expectedWorker) return;
        this.closeWorker();
        this.reloadCamerasAndConnections();
      }, SSE_RECONNECT_DELAY);
    },
    closeWorker() {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
      const worker = this.registeredWorker;
      this.registeredWorker = null;
      this.sseUrl = null;
      if (worker) worker.close();
    },
    // Обработка событий, лента, уведомления и переходы в плеер.

    /** Восстанавливает выбранный режим звуковых оповещений для этого браузера. */
    restoreSoundNotificationsPreference() {
      try {
        this.isSoundNotificationsEnabled = window.localStorage.getItem(SOUND_NOTIFICATIONS_STORAGE_KEY) !== "false";
      } catch (error) {
        devLog("[AircraftEventsMap] Failed to restore sound notification preference", error);
      }
    },
    /** Включает или отключает звук новых событий и сохраняет выбор пользователя. */
    toggleSoundNotifications() {
      this.isSoundNotificationsEnabled = !this.isSoundNotificationsEnabled;
      try {
        window.localStorage.setItem(
          SOUND_NOTIFICATIONS_STORAGE_KEY,
          String(this.isSoundNotificationsEnabled)
        );
      } catch (error) {
        devLog("[AircraftEventsMap] Failed to store sound notification preference", error);
      }
      if (this.isSoundNotificationsEnabled) {
        this.playEventNotificationSound();
      } else {
        this.stopEventNotificationSound();
      }
    },
    /** Синхронизирует переключатель звука между картой и отдельным окном событий. */
    onSoundNotificationsStorageChange(event) {
      if (
        event.storageArea !== window.localStorage
        || event.key !== SOUND_NOTIFICATIONS_STORAGE_KEY
      ) {
        return;
      }
      this.isSoundNotificationsEnabled = event.newValue !== "false";
      if (!this.isSoundNotificationsEnabled) this.stopEventNotificationSound();
    },
    /** Воспроизводит сигнал события и безопасно обрабатывает запрет браузера на autoplay. */
    playEventNotificationSound() {
      const audio = this.$_messageSoundNotificationMixin_audio;
      try {
        audio.currentTime = 0;
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(error => {
            devLog("[AircraftEventsMap] Failed to play sound notification", error);
          });
        }
      } catch (error) {
        devLog("[AircraftEventsMap] Failed to play sound notification", error);
      }
    },
    /** Немедленно останавливает текущий сигнал при отключении оповещений. */
    stopEventNotificationSound() {
      const audio = this.$_messageSoundNotificationMixin_audio;
      audio.pause();
      audio.currentTime = 0;
    },

    /**
     * Привязывает входящее событие к известной камере и добавляет его в ленту.
     * Также обновляет активное событие, при разрешении подаёт звук
     * и в режиме карты запускает подсветку маркера.
     */
    onNewMessage(analyticMessage) {
      if (analyticMessage.analyticType !== ANALYTICS.aircraft) return;
      if (!this.isAircraftEventEnabled(analyticMessage)) return;
      const cameraNumber = String(analyticMessage.cameraNumber ?? "");
      if (!this.sourceCameraNumbers.has(cameraNumber)) return;
      const camera = this.camerasByNumber[cameraNumber];
      if (!camera) return;

      analyticMessage.cameraNumber = cameraNumber;
      analyticMessage.cameraInfo = camera;
      this.$set(
        this.eventCountsByCamera,
        cameraNumber,
        Number(this.eventCountsByCamera[cameraNumber] || 0) + 1
      );
      this.$set(this.lastEventsByCamera, cameraNumber, analyticMessage);
      const eventItem = {
        camera,
        cameraNumber,
        key: `${cameraNumber}:${analyticMessage.id || "event"}:${++this.eventSequence}`,
        message: analyticMessage,
      };
      this.liveEvents.unshift(eventItem);
      if (this.liveEvents.length > EVENTS_LIMIT) {
        this.liveEvents.splice(EVENTS_LIMIT);
      }
      this.activeEventKey = eventItem.key;
      this.activeCameraNumber = cameraNumber;
      if (!this.eventsOnly) {
        this.flashCameraMarker(cameraNumber);
      }
      if (this.isSoundNotificationsEnabled) {
        this.playEventNotificationSound();
      }
    },
    /** Запускает временную пульсацию маркера камеры-источника. */
    flashCameraMarker(cameraNumber) {
      cameraNumber = String(cameraNumber);
      clearTimeout(this.markerFlashTimeoutIds[cameraNumber]);
      this.$set(this.flashingCameraNumbers, cameraNumber, true);
      this.$nextTick(() => {
        if (this.isComponentDestroyed) return;
        const marker = this.getCameraMarker(cameraNumber);
        const tooltip = marker && marker.mapObject && marker.mapObject.getTooltip();
        if (tooltip) tooltip.update();
      });
      const timeoutId = setTimeout(() => {
        this.$delete(this.flashingCameraNumbers, cameraNumber);
        this.$delete(this.markerFlashTimeoutIds, cameraNumber);
      }, MARKER_FLASH_DURATION);
      this.$set(this.markerFlashTimeoutIds, cameraNumber, timeoutId);
    },
    unbindCameraTooltip(cameraNumber) {
      const marker = this.getCameraMarker(cameraNumber);
      marker && marker.mapObject && marker.mapObject.unbindTooltip();
    },
    clearMarkerFlashTimeouts() {
      for (const timeoutId of Object.values(this.markerFlashTimeoutIds)) {
        clearTimeout(timeoutId);
      }
      for (const cameraNumber of Object.keys(this.flashingCameraNumbers)) {
        this.unbindCameraTooltip(cameraNumber);
      }
      this.markerFlashTimeoutIds = {};
      this.flashingCameraNumbers = {};
    },
    /** Формирует переход в архив камеры за восемь секунд до выбранного события. */
    eventPlayerRoute(eventItem) {
      const eventTimestamp = Number(eventItem.message.date);
      const playbackTimestamp = Number.isFinite(eventTimestamp)
        ? Math.max(0, eventTimestamp - EVENT_PLAYBACK_LEAD_MS)
        : eventItem.message.date;

      return {
        name: this.$route.name,
        query: {
          ...(this.$route.query || {}),
          [QUERY_KEY_ONE_SCREEN_CAMERA_NUMBER]: eventItem.cameraNumber,
          [QUERY_KEY_ONE_SCREEN_TIME_SHIFT]: playbackTimestamp,
        },
      };
    },
    /** Выбирает событие и при наличии карты и координат приближает её к камере. */
    selectEvent(eventItem) {
      this.activeEventKey = eventItem.key;
      this.activeCameraNumber = eventItem.cameraNumber;
      if (!this.eventsOnly && !this.isMapSelectionMode && this.hasValidCoordinates(eventItem.camera)) {
        this.focusCamera(eventItem.camera);
      }
    },
    // Изменение ширины и сворачивание ленты событий.

    /** Начинает изменение ширины ленты мышью или касанием. */
    startEventsBarResize(event) {
      if (
        this.eventsOnly
        || this.isEventsBarCollapsed
        || this.isStackedLayout()
        || event.button !== 0
      ) {
        return;
      }

      this.isEventsBarResizing = true;
      this.eventsBarResizeStartX = event.clientX;
      this.eventsBarResizeStartWidth = this.eventsBarWidth;
      this.eventsBarResizePointerId = event.pointerId;
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      document.body.classList.add("aircraft-events-map-events-resizing");
      window.addEventListener("pointermove", this.resizeEventsBar);
      window.addEventListener("pointerup", this.stopEventsBarResize);
      window.addEventListener("pointercancel", this.stopEventsBarResize);
      window.addEventListener("blur", this.stopEventsBarResize);
    },
    /** Обновляет ширину ленты в допустимых пределах во время перетаскивания. */
    resizeEventsBar(event) {
      if (!this.isEventsBarResizing || event.pointerId !== this.eventsBarResizePointerId) return;

      const dragOffset = this.eventsBarResizeStartX - event.clientX;
      const nextWidth = this.clampEventsBarWidth(this.eventsBarResizeStartWidth + dragOffset);
      if (nextWidth === this.eventsBarWidth) return;

      this.eventsBarWidth = nextWidth;
      this.scheduleMapSizeUpdate();
    },
    /** Завершает перетаскивание и синхронизирует размер и overview карты. */
    stopEventsBarResize(event) {
      if (
        event
        && event.pointerId !== undefined
        && this.eventsBarResizePointerId !== null
        && event.pointerId !== this.eventsBarResizePointerId
      ) {
        return;
      }

      const wasResizing = this.isEventsBarResizing;
      this.isEventsBarResizing = false;
      this.eventsBarResizePointerId = null;
      this.removeEventsBarResizeListeners();
      document.body.classList.remove("aircraft-events-map-events-resizing");
      if (wasResizing) this.scheduleMapSizeUpdate(true);
    },
    /** Удаляет глобальные pointer-обработчики изменения ширины ленты. */
    removeEventsBarResizeListeners() {
      window.removeEventListener("pointermove", this.resizeEventsBar);
      window.removeEventListener("pointerup", this.stopEventsBarResize);
      window.removeEventListener("pointercancel", this.stopEventsBarResize);
      window.removeEventListener("blur", this.stopEventsBarResize);
    },
    /** Проверяет, перешёл ли интерфейс в мобильную компоновку без разделителя. */
    isStackedLayout() {
      return window.matchMedia(STACKED_LAYOUT_MEDIA_QUERY).matches;
    },
    /** Вычисляет максимальную ширину ленты с сохранением минимальной ширины карты. */
    getEventsBarMaxWidth() {
      const content = this.$refs.content;
      if (!content || !content.clientWidth) return EVENTS_BAR_MAX_WIDTH;

      const availableWidth = content.clientWidth - MAP_MIN_WIDTH - EVENTS_BAR_RESIZER_WIDTH;
      return Math.max(EVENTS_BAR_MIN_WIDTH, Math.min(EVENTS_BAR_MAX_WIDTH, availableWidth));
    },
    /** Ограничивает запрошенную ширину ленты разрешённым диапазоном. */
    clampEventsBarWidth(width) {
      return Math.max(EVENTS_BAR_MIN_WIDTH, Math.min(this.getEventsBarMaxWidth(), Math.round(width)));
    },
    /** Ограничивает ширину ленты после resize и при её изменении обновляет Leaflet-карту. */
    handleWindowResize() {
      if (this.eventsOnly) return;
      if (this.isStackedLayout()) {
        this.stopEventsBarResize();
        return;
      }

      const nextWidth = this.clampEventsBarWidth(this.eventsBarWidth);
      if (nextWidth === this.eventsBarWidth) return;
      this.eventsBarWidth = nextWidth;
      this.scheduleMapSizeUpdate(true);
    },
    /**
     * Объединяет несколько запросов invalidateSize в один кадр браузера.
     * Флаг updateOverview сохраняется до выполнения отложенного обновления.
     */
    scheduleMapSizeUpdate(updateOverview = false) {
      this.updateOverviewAfterMapResize = this.updateOverviewAfterMapResize || updateOverview;
      this.$nextTick(() => {
        if (this.isComponentDestroyed || this.mapResizeFrameId !== null) return;

        this.mapResizeFrameId = window.requestAnimationFrame(() => {
          this.mapResizeFrameId = null;
          const map = this.$refs.map && this.$refs.map.mapObject;
          if (map) {
            map.invalidateSize({animate: false});
            if (this.updateOverviewAfterMapResize) this.updateMapOverviewState();
          }
          this.updateOverviewAfterMapResize = false;
        });
      });
    },
    /** Сворачивает или раскрывает ленту, перераспределяя место и обновляя размер карты. */
    setEventsBarCollapsed(isCollapsed) {
      if (isCollapsed) this.stopEventsBarResize();
      this.isEventsBarCollapsed = isCollapsed;
      this.scheduleMapSizeUpdate(true);
    },
    // Полноэкранный режим общего контейнера карты и ленты событий.

    /** Возвращает текущий полноэкранный элемент с учётом старого Safari API. */
    getFullscreenElement() {
      return document.fullscreenElement || document.webkitFullscreenElement || null;
    },
    /**
     * Возвращает корень Vue-приложения, внутри которого находятся и карта, и vuedal.
     * Модальное окно может быть видно в native fullscreen только как потомок fullscreen-элемента.
     */
    getFullscreenTarget() {
      return this.$root && this.$root.$el;
    },
    /** Переключает fullscreen для общего корня; CSS оставляет на экране только карту и ленту. */
    async toggleFullscreen() {
      const fullscreenTarget = this.getFullscreenTarget();
      if (!fullscreenTarget) return;

      this.stopEventsBarResize();
      try {
        if (this.getFullscreenElement() === fullscreenTarget) {
          await exitFullscreen();
        } else {
          await requestFullscreenByElement(fullscreenTarget);
        }
      } catch (error) {
        devLog("[AircraftEventsMap] Failed to toggle fullscreen", error);
      }
    },
    /** Синхронизирует кнопку и размер Leaflet-карты после входа, выхода или Esc. */
    handleFullscreenChange() {
      this.isFullscreen = this.getFullscreenElement() === this.getFullscreenTarget();
      this.handleWindowResize();
      this.scheduleMapSizeUpdate(true);
    },
    // Навигация и определение текущего состояния карты.

    /** Возвращает карту к границам, в которых видны все найденные камеры. */
    showMapOverview() {
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (!map || !this.bounds || !this.bounds.isValid()) return;

      clearTimeout(this.focusTimeoutId);
      this.activeCameraNumber = null;
      map.closePopup();

      if (this.mapCameras.length === 1) {
        map.flyTo(this.cameraLatLng(this.mapCameras[0]), this.zoom, {animate: true, duration: 0.6});
        return;
      }
      map.flyToBounds(this.bounds, {animate: true, duration: 0.6, padding: [24, 24]});
    },
    /**
     * Определяет общий вид: все камеры должны находиться во viewport,
     * а zoom не должен быть крупнее значения, рассчитанного для их общих границ.
     */
    updateMapOverviewState() {
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (!map || !this.bounds || !this.bounds.isValid() || this.mapCameras.length === 0) {
        this.isMapInOverview = true;
        return;
      }

      const visibleBounds = map.getBounds();
      const allCamerasAreVisible = this.mapCameras.every(camera => visibleBounds.contains(this.cameraLatLng(camera)));
      const calculatedOverviewZoom = this.mapCameras.length === 1
        ? Number(this.zoom)
        : map.getBoundsZoom(this.bounds);
      const overviewZoom = Number.isFinite(calculatedOverviewZoom) ? calculatedOverviewZoom : Number(this.zoom);
      this.isMapInOverview = allCamerasAreVisible && map.getZoom() <= overviewZoom;
    },
    /** Центрирует карту на камере и открывает её popup после завершения flyTo. */
    focusCamera(camera) {
      const map = this.$refs.map && this.$refs.map.mapObject;
      if (!map) return;

      this.isMapInOverview = false;
      const cameraNumber = String(camera.number);
      const targetZoom = Math.max(map.getZoom(), EVENT_FOCUS_ZOOM);
      map.flyTo(this.cameraLatLng(camera), targetZoom, {animate: true, duration: 0.7});

      clearTimeout(this.focusTimeoutId);
      this.focusTimeoutId = setTimeout(() => {
        // Проверяем выбор ещё раз, чтобы старый таймер не открыл popup другой камеры.
        if (this.isComponentDestroyed || this.activeCameraNumber !== cameraNumber) return;
        const marker = this.getCameraMarker(cameraNumber);
        marker && marker.mapObject && marker.mapObject.openPopup();
      }, 750);
    },
  },
};
</script>

<style lang="scss">
@use "./scss/aircraft-events-map.scss";
</style>
